import { Product } from "@thenounproject/lingo-core";
import remove from "lodash/remove";
import createEntityReducer from "../helpers/createEntityReducer";
import { getEntitiesFromAction, mergeEntitiesIfChanged } from "../helpers/mergeIfChanged";
import { deleteProduct } from "@redux/actions/products/useDeleteProduct";
import { fetchProducts } from "@redux/actions/products/useProducts";
import { updateProduct } from "@redux/actions/products/useUpdateProduct";
import { invalidateQuery } from "@redux/actions/actionCreators/createQueryAction";

const mergeProducts = (objValue, srcValue, key) => {
  // Replace `fields` and `variants` wholesale rather than letting lodash merge
  // arrays by index (which would leave removed multiselect values in place).
  if (key === "fields" || key === "variants" || key === "assetUuids") return srcValue ?? objValue;
  return undefined;
};

export default createEntityReducer<Product>(
  "products",
  queryBuilder => {
    queryBuilder.addCase(deleteProduct.fulfilled, (state, action) => {
      const productId = action.payload.result;
      const { spaceId } = action.meta.arg;
      fetchProducts.getQueryData(state, { spaceId }).forEach(query => {
        if (!query.data) return;
        const removed = remove(query.data.products, id => id === productId);
        query.data.total -= removed.length;
      });
    });
    // The update response omits preview_asset, so invalidate the list query so
    // the next products table mount refetches with the correct preview image.
    queryBuilder.addCase(updateProduct.fulfilled, (state, action) => {
      const { spaceId } = action.meta.arg;
      fetchProducts.getQueryData(state, { spaceId }).forEach(query => {
        invalidateQuery(query);
      });
    });
  },
  objectBuilder => {
    // The update response omits preview_asset — skip it during merge so a save
    // never clears an existing thumbnail. The invalidated list query (above)
    // brings the correct value back on the next products table mount.
    objectBuilder.addCase(updateProduct.fulfilled, (state, action) => {
      const products = getEntitiesFromAction<Product>(action, "products");
      return mergeEntitiesIfChanged(state, products, (objValue, srcValue, key) => {
        if (key === "previewAsset") return objValue;
        return mergeProducts(objValue, srcValue, key);
      });
    });
    objectBuilder.addDefaultCase((state, action) => {
      const products = getEntitiesFromAction<Product>(action, "products");
      return mergeEntitiesIfChanged(state, products, mergeProducts);
    });
  }
);
